import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, ArrowRight } from "@phosphor-icons/react";

export default function ClubAverages() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/pro/club-averages")
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 402) setDenied(true);
      });
  }, []);

  if (denied || (user && !user.is_pro)) {
    return (
      <div className="max-w-md mx-auto py-10">
        <div className="card-default p-8 text-center" data-testid="pro-paywall">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#0F2537] text-white flex items-center justify-center">
            <Crown weight="fill" size={28} />
          </div>
          <h1 className="font-heading font-black text-2xl mt-4">Bula Pro feature</h1>
          <p className="text-[#0F2537]/70 mt-2">Club averages are part of Bula Pro. Upgrade to unlock club-by-club tendencies.</p>
          <Link to="/pricing" data-testid="paywall-cta-pricing"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E05D3A] text-white font-bold">
            See plans <ArrowRight weight="bold" size={14} />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="py-10">Loading…</div>;

  const rows = Object.entries(data.by_par)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow inline-flex items-center gap-1.5">
          <Crown size={11} weight="fill" className="text-[#E05D3A]" /> Pro
        </span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Club tendencies</h1>
        <p className="text-[#0F2537]/70 mt-2">Your average per hole-par across {data.total_holes} holes played.</p>
      </div>

      {rows.length === 0 ? (
        <div className="card-default p-10 text-center text-[#0F2537]/60">No rounds yet.</div>
      ) : (
        <div className="card-default overflow-hidden">
          <table className="w-full text-sm" data-testid="club-averages-table">
            <thead>
              <tr className="text-[#0F2537]/60 text-xs uppercase tracking-widest border-b border-[#E2E0D8]">
                <th className="text-left px-5 py-3">Par</th>
                <th className="text-right px-5 py-3">Holes</th>
                <th className="text-right px-5 py-3">Avg</th>
                <th className="text-right px-5 py-3">vs Par</th>
                <th className="text-right px-5 py-3 hidden sm:table-cell">Best</th>
                <th className="text-right px-5 py-3 hidden sm:table-cell">Worst</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, s]) => {
                const par = parseInt(key.replace("par_", ""), 10);
                const vs = s.avg - par;
                return (
                  <tr key={key} className="border-b border-[#E2E0D8] last:border-0">
                    <td className="px-5 py-4 font-heading font-black text-2xl">Par {par}</td>
                    <td className="px-5 py-4 text-right">{s.n}</td>
                    <td className="px-5 py-4 text-right font-heading font-bold text-xl">{s.avg.toFixed(2)}</td>
                    <td className={`px-5 py-4 text-right font-bold ${vs <= 0 ? "text-[#1E5631]" : "text-[#E05D3A]"}`}>
                      {vs >= 0 ? "+" : ""}{vs.toFixed(2)}
                    </td>
                    <td className="px-5 py-4 text-right hidden sm:table-cell">{s.min}</td>
                    <td className="px-5 py-4 text-right hidden sm:table-cell">{s.max}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
